import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import cajaUsuarioService, { CajaUsuarioPagedResult } from '../../services/cajaUsuarioService';
import grupoMcpService from '../../services/grupoMcpService';

const CajaxUsuario: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [cajasData, setCajasData] = useState<CajaUsuarioPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [nombresCaja, setNombresCaja] = useState<Record<number, string>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [_loading, setLoading] = useState(false);

  const enrichNombresCaja = async (items: CajaUsuarioPagedResult['items']) => {
    const codigos = items
      .map((i) => i.codigoCaja)
      .filter((codigo, idx, arr) => codigo > 0 && arr.indexOf(codigo) === idx);

    const map: Record<number, string> = {};
    await Promise.all(
      codigos.map(async (codigo) => {
        const existente = items.find((i) => i.codigoCaja === codigo)?.nombreCaja?.trim();
        if (existente) {
          map[codigo] = existente;
          return;
        }
        try {
          const g = await grupoMcpService.getGrupoMcp(codigo);
          map[codigo] = g?.descripcion?.trim() || '-';
        } catch {
          map[codigo] = '-';
        }
      })
    );
    setNombresCaja(map);
  };

  const fetchCajasUsuario = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const response = await cajaUsuarioService.getCajasUsuario({
        pageNumber,
        pageSize,
        searchTerm: search || undefined
      });
      setCajasData(response);
      await enrichNombresCaja(response.items);
    } catch (error: any) {
      console.error('Error al cargar cajas por usuario:', error);
      enqueueSnackbar('Error al cargar cajas por usuario', { variant: 'error' });
      setCajasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
      setNombresCaja({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCajasUsuario(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (codigoCaja: number, usuario: string) => {
    if (window.confirm('¿Está seguro de eliminar esta asignación?')) {
      try {
        await cajaUsuarioService.deleteCajaUsuario(codigoCaja, usuario);
        enqueueSnackbar('Asignación eliminada exitosamente', { variant: 'success' });
        fetchCajasUsuario(page + 1, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar asignación:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar asignación', { variant: 'error' });
      }
    }
  };

  const nombreCajaDe = (codigoCaja: number, fallback?: string) =>
    fallback?.trim() || nombresCaja[codigoCaja] || '...';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Caja por Usuario
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/sistema/caja-usuario/nuevo')}>
              Nueva Asignación
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {cajasData.items.map((caja) => (
                <Card key={`${caja.codigoCaja}-${caja.usuario}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Caja {caja.codigoCaja} - {nombreCajaDe(caja.codigoCaja, caja.nombreCaja)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Usuario: {caja.usuario}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/sistema/caja-usuario/${caja.codigoCaja}/${caja.usuario}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(caja.codigoCaja, caja.usuario)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código Caja</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre Caja</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre Usuario</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cajasData.items.map((caja) => (
                    <TableRow key={`${caja.codigoCaja}-${caja.usuario}`} hover>
                      <TableCell>{caja.codigoCaja}</TableCell>
                      <TableCell>{nombreCajaDe(caja.codigoCaja, caja.nombreCaja)}</TableCell>
                      <TableCell>{caja.usuario}</TableCell>
                      <TableCell>{caja.nombreUsuario || '-'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/sistema/caja-usuario/${caja.codigoCaja}/${caja.usuario}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(caja.codigoCaja, caja.usuario)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={cajasData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CajaxUsuario;
